/** @jsx isolateComponent */
import React from 'react';
import { css } from '@emotion/react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { DateTime } from '@zapier/date';
import type { Dateable } from '@zapier/date';

import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import {
  Animation,
  Colors,
  ColorName,
  Shadows,
  Typography,
} from '../../../theme';
import { Avatar, AvatarsWithText, AvatarText } from '../../display/Avatar';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Img } from '../../display/Img';
import { Link } from '../../navigation/Link';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';

type Author = {
  name: string;
  imageUrl?: string;
};

/**
 * enforce the following relationship between the author prop and the meta prop:
 *   - if no author prop exists, you cannot pass the meta prop
 *   - if author prop exists, you can pass OR omit the meta prop
 */
type AuthorMetaInteractionProps =
  | {
      meta?: never;
    }
  | { author: Author; meta?: React.ReactNode };

export type Props = {
  /**
   * The `src` to use for `Article`'s image.
   */
  imageUrl?: string;
  /**
   * Optional category assigned to `Article`.
   */
  category?: string;
  /**
   * URL to page containing other articles within `category`.
   * If `category` is supplied, this should be supplied, too.
   */
  categoryUrl?: string;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   * If supplied, will take the place of the category link
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   * @default "fileFilled"
   */
  labelIcon?: IconName;
  /**
   * The labelVariant determines the background-color of the label.
   * @default "neutral"
   */
  labelVariant?: 'primary' | 'secondary' | 'neutral';
  /**
   * Title of `Article`.
   */
  title: string;
  /**
   * A brief description of `Article` to complement `title`.
   */
  description?: React.ReactNode;
  /**
   * How to lay out `Article`. `card` lays out all content vertically
   * with the image above the text, while `row` lays the image out
   * to the side of the text.
   */
  layout: 'card' | 'row';
  /**
   * Meta info about the article, typically how long it takes
   * to read it and/or the date that it was published. Use
   * the `ArticleMeta` component with `ArticleReadTime` and/or
   * `ArticleDate` as its children to render the typical content.
   * Render custom children if those don't suffice.
   */
  meta?: React.ReactNode;
  /**
   * URL to where `Article` links.
   */
  url: string;
  /**
   * Information about the author of `Article`.
   */
  author?: Author;
  /**
   * @deprecated The `size` of `Article` which impacts its typography,
   * image size, spacing, etc. All Articles render as size medium now.
   */
  size: 'small' | 'medium' | 'large';
} & AuthorMetaInteractionProps;

type LabelBackgroundConfig = {
  [key: string]: ColorName;
};

const labelBackgroundColorConfig: LabelBackgroundConfig = {
  primary: 'blue10',
  secondary: 'peach',
  neutral: 'neutral300',
};

const Vars = createUniqueCustomProperties('Article', [
  'imageMarginBottom',
  'titleMargin',
  'labelBackgroundColor',
]);

const Styles = labelStyles('Article', {
  root: css`
    display: flex;
    position: relative;
    background: ${Colors.neutral100};
    border: 1px solid ${Colors.neutral400};
    transition-property: box-shadow;
    transition-timing-function: ${Animation.transitionTimingFunction};
    transition-duration: ${Animation.transitionDuration};
    border-radius: 3px;
    &:hover,
    &:focus,
    &:focus-within {
      color: ${Colors.neutral800};
      box-shadow: ${Shadows.elevation10};
    }

    &[data-layout='card'] {
      flex-direction: column;
    }

    &[data-layout='row'] {
      flex-direction: row;
      align-items: center;
      padding: 30px 0;
    }

    &[data-size='medium'] {
      ${Vars.imageMarginBottom}: 25px;
      ${Vars.titleMargin}: 20px;
      height: max-content;
    }
  `,
  image: css`
    position: relative;
    overflow: hidden;
    border-radius: 3px 3px 0 0;

    &[data-layout='card'] {
      margin-bottom: var(${Vars.imageMarginBottom});
      padding-bottom: 50%; // 2:1 ratio
    }

    &[data-layout='row'] {
      border-radius: 3px;
      margin-left: 30px;
      margin-right: 30px;
      width: 370px;
      height: 185px; // 2:1 ratio
    }
  `,
  // `Styles.image` creates a box with a 2:1 ratio as defined
  // by design and this node centers the image within that box.
  imageInner: css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: 100%;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    border-radius: 0 0 3px 3px;
    padding: 0 20px 30px;
    flex: unset;

    &[data-showimg='false'] {
      padding: 30px 20px;
    }

    &[data-layout='row'] {
      padding: 0;
      border-radius: 0;
      flex: 1;
    }

    /* if the card is in row layout with no image we need to apply horizontal padding to the content */
    &[data-rownoimg='true'] {
      padding: 0 30px;
    }
  `,
  title: css`
    &[class] {
      order: 2;
      ${Typography.sectionHeader3Semibold};
      color: ${Colors.neutral800};
      margin-bottom: var(${Vars.titleMargin});
      transition: ${Animation.transitionValue};
      text-decoration: none;
      &:hover,
      &:focus {
        color: ${Colors.neutral800};
      }

      // This pseudo element stretches over the entire Article
      // to make the entire Article clickable while preserving
      // accessibilty by not nesting <a /> tags.
      &::before {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }

      &[data-lastelement='true'] {
        margin-bottom: 0;
      }
    }
  `,
  metaInfo: css`
    // Ensure meta info renders above rest of clickable Article
    // in case any of the meta info is interactive.
    position: relative;
    z-index: 2;
    order: 4;
    display: flex;
    align-items: center;
    color: ${Colors.gray60};
    ${Typography.smallPrint1};
  `,
  meta: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  metaItem: css`
    display: flex;
    align-items: center;
    &:not(:last-child) {
      &::after {
        content: '•';
        margin: 0 5px;
      }
    }
  `,
  description: css`
    order: 3;
    margin-bottom: 20px;
    color: ${Colors.neutral700};

    &[data-size='medium'] {
      ${Typography.paragraph3};
    }

    &[data-layout='row'] {
      ${Typography.paragraph1Short};
    }

    &[data-lastelement='true'] {
      margin-bottom: 0;
    }
  `,
  category: css`
    order: 1;
    position: relative;
    // Ensure clickable category renders above rest of clickable Article
    z-index: 2;
    ${Typography.smallPrint1Bold};
    margin-bottom: 10px;
  `,
  label: (labelVariant: Props['labelVariant'] = 'neutral') => css`
    order: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    text-align: center;
    ${Vars.labelBackgroundColor}: ${Colors[
      labelBackgroundColorConfig[labelVariant]
    ]};
    background-color: var(${Vars.labelBackgroundColor});
    border-radius: 100px;
    ${Typography.paragraph3Semibold};
    color: ${Colors.neutral800};
    padding: 4px 8px;
    margin-bottom: 10px;
    width: max-content;
  `,
});

const ArticleTag = ({
  label,
  labelIcon = 'fileFilled',
  labelVariant = 'neutral',
}: Pick<Props, 'label' | 'labelIcon' | 'labelVariant'>) => {
  return (
    <span css={Styles.label(labelVariant)}>
      {labelIcon && <Icon name={labelIcon} size={18} />}
      <span>{label}</span>
    </span>
  );
};

/**
 * Renders an article preview which contains a title, image, and publishing
 * information: date, category, and author details.
 */
export const Article = (props: Props) => {
  return (
    <article css={Styles.root} data-layout={props.layout} data-size="medium">
      {props.imageUrl && (
        <div css={Styles.image} data-layout={props.layout}>
          <div css={Styles.imageInner}>
            <Img
              // `aria-hidden` and empty `alt` are applied since we do not describe
              // our images for screen readers. Understanding the content of the image
              // is not critical to understanding the content of the article.
              alt=""
              ariaHidden={true}
              aspectRatio="2:1"
              borderRadius={0}
              isBlock={true}
              loading="lazy"
              objectFit="cover"
              sizes="100vw"
              src={props.imageUrl}
            />
          </div>
        </div>
      )}
      <div
        css={Styles.content}
        data-layout={props.layout}
        data-showimg={!!props.imageUrl}
        data-rownoimg={props.layout === 'row' && !props.imageUrl}
      >
        <HeadingLevel>
          <Link
            css={Styles.title}
            color={null}
            href={props.url}
            data-lastelement={!props.author && !props.description}
          >
            <Heading>{props.title}</Heading>
          </Link>
        </HeadingLevel>
        {props.description && (
          <div
            css={Styles.description}
            data-size="medium"
            data-layout={props.layout}
            data-lastelement={!props.author}
          >
            {props.description}
          </div>
        )}
        {props.author && (
          <div css={Styles.metaInfo}>
            <AvatarsWithText
              avatars={
                <Avatar
                  ariaHidden={true}
                  isBlock={true}
                  name={props.author.name}
                  size={40}
                  url={props.author.imageUrl}
                />
              }
              multiline={true}
              size={40}
            >
              <AvatarText ariaLabel={`by ${props.author.name}`} kind="heavy">
                {props.author.name}
              </AvatarText>
              {props.meta}
            </AvatarsWithText>
          </div>
        )}
        {props.label && (
          <ArticleTag
            label={props.label}
            labelIcon={props.labelIcon}
            labelVariant={props.labelVariant}
          />
        )}
        {props.category && props.categoryUrl && !props.label && (
          <div css={Styles.category}>
            <Link
              ariaLabel={`Categorized under ${props.category}`}
              href={props.categoryUrl}
            >
              {props.category}
            </Link>
          </div>
        )}
      </div>
    </article>
  );
};

export const ArticleMeta = (props: { children: React.ReactNode }) => {
  const children = React.Children.toArray(props.children)
    .filter(Boolean)
    .map((child, i) => {
      return (
        <span css={Styles.metaItem} key={i}>
          {child}
        </span>
      );
    });
  return (
    <AvatarText kind="light">
      <span css={Styles.meta}>{children}</span>
    </AvatarText>
  );
};

export const ArticleReadTime = (props: { minutes: number }) => {
  return <div>{props.minutes} min read</div>;
};

export const ArticleDate = (props: { date: Dateable }) => {
  return <DateTime date={props.date} />;
};
